import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import {
  GanttChart, OutlineView, Stat,
  buildGrid, buildPhaseRows, buildWorkstreamRows,
} from './ProjectSchedule';

// Read-only customer view of a project's schedule -- the pilot screen for the customer portal
// (Jeremy, 2026-09-01; rebuilt 2026-09-02 to reuse the real chart instead of a simplified summary,
// per Jeremy's ask for "just like the internal views, without edit rights, no Plan (edit) view").
//
// Reuses GanttChart/OutlineView/Stat/buildGrid/buildPhaseRows/buildWorkstreamRows directly from
// ProjectSchedule.js -- those are pure presentation given rows/grid, with no write logic of their
// own (every write lives in ScheduleBoard's edit popovers, which this file never renders). Passing
// `readOnly` down only swaps a "click to edit" tooltip; selecting a row still highlights it, since
// highlighting isn't itself an edit.
//
// `apiFetch`'s /projects call already comes back scoped to this contact's ExternalUserProject
// grants (ProjectsController, apps/api) -- there is no client-side filtering here to keep in sync
// with the server, because there's nothing to filter: the list IS the grant.

export default function CustomerProjectSchedule() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await apiFetch('/projects');
        if (cancelled) return;
        setProjects(rows);
        // A single-project customer opens straight onto it -- no picker to click through for the
        // common case of one grant. Someone with more than one project still has to choose.
        if (rows.length === 1) setProjectId(rows[0].id);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!projectId) { setSchedule(null); return; }
    let cancelled = false;
    setLoading(true);
    setError('');
    apiFetch(`/gantt/${projectId}`)
      .then((data) => { if (!cancelled) setSchedule(data); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services · Project Schedule</div>
        <h1 className="page-title">{schedule?.project?.name || 'Project Schedule'}</h1>
        <p className="page-subtitle">Phases, milestones, tasks and dependencies for your project.</p>
      </div>

      {projects.length > 1 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="form-group" style={{ marginBottom: 0, maxWidth: 420 }}>
            <label className="form-label" htmlFor="customer-schedule-project">Project</label>
            <select
              id="customer-schedule-project"
              className="form-input"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">— Select a project —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {error && (
        <div className="card" style={{ marginBottom: 20, borderLeft: '4px solid var(--red)' }} role="alert">
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            <strong style={{ color: 'var(--red)' }}>Couldn't load the schedule.</strong> {error}
          </div>
        </div>
      )}

      {loading && !schedule && (
        <div className="card" style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
      )}

      {!loading && !projects.length && !error && (
        <div className="placeholder-page">
          <div className="placeholder-icon">🗂️</div>
          <div className="placeholder-title">No projects available yet</div>
          <div className="placeholder-sub">Reach out to your Aumentum contact if this doesn't look right.</div>
        </div>
      )}

      {!loading && !error && projects.length > 1 && !projectId && (
        <div className="placeholder-page">
          <div className="placeholder-icon">📋</div>
          <div className="placeholder-title">Pick a project</div>
        </div>
      )}

      {schedule && <ReadOnlyScheduleBoard schedule={schedule} />}
    </div>
  );
}

// The read-only counterpart to ProjectSchedule.js's ScheduleBoard: same row-building, same chart,
// same Phase/Workstream toggle and collapse-all -- but no toolbar (no Populate from SOW, no
// +Phase/+Task/+Milestone/+Dependency), no "Plan (edit)" view, and no edit popovers on selection.
function ReadOnlyScheduleBoard({ schedule }) {
  const { phases, workstreams = [], tasks, milestones, dependencies, window: dateWindow } = schedule;
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState(null);
  const [view, setView] = useState('phase'); // 'phase' | 'workstream' -- no 'plan' here.

  // Same "start fully collapsed" convention as the internal board (Jeremy, 2026-08-25) -- a real
  // plan is 100+ rows, and collapsed bands are the overview.
  const [collapsed, setCollapsed] = useState(
    () => new Set([...workstreams.map((w) => w.id), 'unassigned']),
  );
  const [collapsedPhases, setCollapsedPhases] = useState(
    () => new Set([...phases.map((p) => p.id), 'unassigned']),
  );

  const activeCollapsed = view === 'phase' ? collapsedPhases : collapsed;
  const setActiveCollapsed = view === 'phase' ? setCollapsedPhases : setCollapsed;

  const toggleCollapsed = (id) =>
    setActiveCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Selecting still highlights the row (same visual as the internal board) -- there's just no
  // popover to open on top of it.
  const selectTask = (id) => {
    setSelectedMilestoneId(null);
    setSelectedTaskId((prev) => (id === prev ? null : id));
  };
  const selectMilestone = (id) => {
    setSelectedTaskId(null);
    setSelectedMilestoneId((prev) => (id === prev ? null : id));
  };

  const rows = useMemo(
    () =>
      view === 'phase'
        ? buildPhaseRows(phases, tasks, milestones, collapsedPhases)
        : buildWorkstreamRows(workstreams, tasks, milestones, collapsed),
    [view, phases, workstreams, tasks, milestones, collapsed, collapsedPhases],
  );

  const groupIds = rows.filter((r) => r.collapsible).map((r) => r.id);
  const allCollapsed = groupIds.length > 0 && groupIds.every((id) => activeCollapsed.has(id));
  const toggleAll = () => setActiveCollapsed(allCollapsed ? new Set() : new Set(groupIds));
  const grid = useMemo(() => buildGrid(dateWindow), [dateWindow]);

  const hasSchedule = [phases, workstreams, milestones, tasks, dependencies].some((c) => c.length > 0);

  return (
    <>
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
          <Stat label="Phases" value={phases.length} />
          <Stat label="Tasks" value={tasks.length} />
          <Stat label="Milestones" value={milestones.length} />
          <Stat label="Dependencies" value={dependencies.length} />
        </div>
      </div>

      <div className="card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 4,
            flexWrap: 'wrap',
          }}
        >
          {groupIds.length > 0 && (
            <button
              className="btn btn-outline"
              style={{ fontSize: 11, padding: '3px 10px' }}
              onClick={toggleAll}
            >
              {allCollapsed ? 'Expand all' : 'Collapse all'}
            </button>
          )}
          {/* Phase/Workstream only -- no "Plan (edit)" tab. */}
          <div style={{ display: 'flex', gap: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            {[
              { key: 'phase', label: 'Phase view' },
              { key: 'workstream', label: 'Workstream view' },
            ].map((option) => (
              <button
                key={option.key}
                onClick={() => setView(option.key)}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '6px 14px',
                  border: 'none',
                  cursor: 'pointer',
                  background: view === option.key ? 'var(--primary-dark)' : 'var(--surface)',
                  color: view === option.key ? 'var(--white)' : 'var(--text-mid)',
                }}
                aria-pressed={view === option.key}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {view === 'workstream' && !workstreams.length && (
          <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 10 }}>
            No workstreams on this project's schedule. Anything without one shows under Unassigned below.
          </div>
        )}

        {!hasSchedule ? (
          <div style={{ fontSize: 13, color: 'var(--text-mid)', fontStyle: 'italic', padding: '12px 0' }}>
            Nothing scheduled yet. Check back once your project's schedule has been laid out.
          </div>
        ) : !rows.length ? (
          <div style={{ fontSize: 13, color: 'var(--text-mid)', fontStyle: 'italic', padding: '12px 0' }}>
            Nothing in this view yet.
          </div>
        ) : !grid ? (
          <OutlineView
            rows={rows}
            selectedTaskId={selectedTaskId}
            selectedMilestoneId={selectedMilestoneId}
            onSelectTask={selectTask}
            onSelectMilestone={selectMilestone}
            onToggleCollapsed={toggleCollapsed}
            readOnly
          />
        ) : (
          <GanttChart
            rows={rows}
            grid={grid}
            view={view}
            dependencies={dependencies}
            selectedTaskId={selectedTaskId}
            selectedMilestoneId={selectedMilestoneId}
            onSelectTask={selectTask}
            onSelectMilestone={selectMilestone}
            onToggleCollapsed={toggleCollapsed}
            readOnly
          />
        )}
      </div>
    </>
  );
}
